import MenuItem from '@mui/material/MenuItem'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** API */
import TemplateDoctorNotesApi from 'api/setting/treatments/template-doctor-note.api'

/** STYLE */
import { Box, Grid, Switch } from '@mui/material'
import { styled } from '@mui/material/styles'
import useStyles from 'features/setting/treatments/template-doctor-note/style'

/** UTILS || SLICE || CONSTANTS */
import { resetModal, showModal, submitModal, unSubmitModal } from 'app/slice/modal.slice'
import { onImgError } from 'constants/images'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { fileUrl } from 'api/api'
import { swalActive } from 'component/Alert/Swal'
import ButtonCustom from 'component/Button/ButtonCustom'
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputTextField from 'component/Input/InputTextField'
import InputTextSearch from 'component/Input/InputTextSearch'
import ModalCustom from 'component/ModalCustom/ModalCustom'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import FilterSelect from 'component/Select/FilterSelect'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import UploadImage from 'component/Upload/UploadImage'

export const TableWidth = styled(Box)(({ theme }) => ({
  '.MuiTable-root': {
    '.MuiTableBody-root': {
      '.MuiTableCell-root': {
        '&:nth-of-type(5)': {
          paddingTop: 9
        },
        '&:nth-of-type(6)': {
          paddingTop: 9
        },
        '&:nth-of-type(7)': {
          paddingTop: 9
        }
      }

    }
  }
}))

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}
const initStateErrorMessage = {
  NOT_FOUND_BANK: ``,
  BANK_ALREADY_SYSTEM: ``,
  INVALID_CREATED: ``,
  INVALID_UPDATED: ``,

  TEMPLATE_DOCTOR_NOTE_NAME: ``,
  IS_DUPLICATE_TEMPLATE_DOCTOR_NOTE_NAME: ``,
  TEMPLATE_DOCTOR_NOTE_NAME_STRING_BASE: ``,
  TEMPLATE_DOCTOR_NOTE_NAME_STRING_EMPTY: ``,
  TEMPLATE_DOCTOR_NOTE_NAME_MAXIMUM_LENGTH: ``,
  TEMPLATE_DOCTOR_NOTE_NAME_ANY_REQUIRED: ``,
}
export default function TemplateDoctorNote() {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [templates, setTemplates] = useState([])

  const [templateId, setTemplateId] = useState<number>(0)
  const [templateName, setTemplateName] = useState('')
  const [templateStatus, setTemplateStatus] = useState('NOT_SUPPORT')
  const [image, setImage] = useState({
    fileId: null,
    base64: '',
    fileExtension: '',
    fileSize: ''
  })
  const [imgData, setImgData]: any = useState()
  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(5)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const loadData = useCallback(async () => {

    let condition: any = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (status) condition = { ...condition, status: status === 'ALL' ? '' : status }

    const res = await TemplateDoctorNotesApi.findAll(condition)
    if (res.status === 200) {
      setRowCount(res.headers['x-total'])
      setTemplates(res.data)
    }
  }, [page, pageLimit, search, sortBy, sortType, status])

  useEffect(() => { loadData() }, [loadData])

  const submit = () => {
    if (!templateName) {
      return setErrorMessage({ ...errorMessage, ...{ TEMPLATE_DOCTOR_NOTE_NAME_STRING_EMPTY: !templateName ? t(`TEMPLATE_DOCTOR_NOTE.MESSAGE.TEMPLATE_DOCTOR_NOTE_NAME_STRING_EMPTY`) : '' } })
    }

    dispatch(submitModal())
    TemplateDoctorNotesApi.create({ templateName, image })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t(`TEMPLATE_DOCTOR_NOTE.MESSAGE.SUCCESS.CREATE`), '', null, null)
          resetForm()

          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`TEMPLATE_DOCTOR_NOTE.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`TEMPLATE_DOCTOR_NOTE.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const submitEdit = () => {
    if (!templateName) {
      return setErrorMessage({ ...errorMessage, ...{ TEMPLATE_DOCTOR_NOTE_NAME_STRING_EMPTY: !templateName ? t(`TEMPLATE_DOCTOR_NOTE.MESSAGE.TEMPLATE_DOCTOR_NOTE_NAME_STRING_EMPTY`) : '' } })
    }

    dispatch(submitModal())
    TemplateDoctorNotesApi.update(templateId, { templateName, status: templateStatus, image })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`TEMPLATE_DOCTOR_NOTE.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`TEMPLATE_DOCTOR_NOTE.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`TEMPLATE_DOCTOR_NOTE.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setTemplateId(0)
    setTemplateName('')
    setImgData('')
    clearErrorMessage()
  }
  const onActive = (id: number, valueStatus: string) => {
    swalActive(
      `<p class="mb-0 mx-auto" style="max-width: 325px">${t('TEMPLATE_DOCTOR_NOTE.ALERT.CONFIRM_STATUS')}</p>`,
      null, (res: any) => {
        if (res) {
          TemplateDoctorNotesApi.updateStatus(id, valueStatus)
            .then((resp) => {
              notiSuccess(t('TEMPLATE_DOCTOR_NOTE.MESSAGE.SUCCESS.UPDATE_STATUS'))
              loadData()
            })
            .catch((e) => {
              notiError(t('TEMPLATE_DOCTOR_NOTE.MESSAGE.ERROR'))
            })
        }
      }, null, t('BUTTON.SUBMIT'), t('BUTTON.CANCEL'))
  }
  const onDelete = (data: any) => {
    const { templateDoctorNoteId: tempId, templateName: tempName } = data

    swalActive(
      `<p class="mb-0 mx-auto" style="max-width: 325px">${t('TEMPLATE_DOCTOR_NOTE.ALERT.CONFIRM_DELETE')}</p>`,
      `<p class="mb-0 mx-auto" style="max-width: 325px">${t('TEMPLATE_DOCTOR_NOTE.ALERT.CONFIRM_DELETE_', {
        templateName: tempName
      })}</p>`,
      (res: any) => {
        if (res) {
          TemplateDoctorNotesApi.remove(tempId)
            .then((resp) => {
              notiSuccess(t('TEMPLATE_DOCTOR_NOTE.MESSAGE.SUCCESS.DELETE'))
              loadData()
            })
            .catch((e) => {
              notiError(t('TEMPLATE_DOCTOR_NOTE.MESSAGE.ERROR'))
            })
        }
      }, null, t('BUTTON.SUBMIT'), t('BUTTON.CANCEL')
    )
  }

  const onEdit = (data: any) => {
    const { templateDoctorNoteId: tempId, templateName: tempName, url: tempUrl, status: tempStatus } = data
    setTemplateId(tempId)
    setTemplateName(tempName)
    setTemplateStatus(tempStatus)
    if (image) {
      setImgData(`${fileUrl}/${tempUrl}`)
      setImage({ fileId: null, base64: '', fileExtension: '', fileSize: '' })
    }
    dispatch(showModal())
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const uploadImage = (files: any) => {
    if (files[0]) {
      /**
       * files all detail file
       * lastModified, lastModifiedDate
       * name, size, type
       */
      const file = files[0]
      const fileName = file.name.split('.')
      const detailImage = {
        fileId: null,
        base64: '',
        fileExtension: fileName[fileName.length - 1],
        fileSize: file.size.toString()
      }
      const reader: any = new FileReader()
      reader.addEventListener('load', () => {
        /** reader.result is base64 */
        const base64 = reader.result.split(',')
        setImgData(reader.result)
        detailImage.base64 = base64.length > 0 ? base64[base64.length - 1] : reader.result
      })
      reader.readAsDataURL(file)
      setImage(detailImage)
    }
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: t('TABLE.NO') },
    { id: 'bankLogo', disablePadding: false, align: 'center', label: t('TEMPLATE_DOCTOR_NOTE.TABLE.CELL.IMAGE_TEMPLATE') },
    { id: 'bankName', disablePadding: false, label: t('TEMPLATE_DOCTOR_NOTE.TABLE.CELL.TEMPLATE_NAME') },
    { id: 'status', disablePadding: false, label: t('TEMPLATE_DOCTOR_NOTE.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('TEMPLATE_DOCTOR_NOTE.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { templateDoctorNoteId: tempId, templateName: tempName, url: tempUrl, isDefault: tempIsDefault, status: tempStatus, updatedBy, updatedAt } = objData
    const statusBtnActive = tempStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = tempStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled

    const objRenderData = {
      key: tempId,
      id: tempId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        {
          option: 'COMPONENT', align: 'center', component: (
            <>
              <img alt="" style={{ width: '75px', height: '75px', objectFit: 'cover' }} src={`${fileUrl}/${tempUrl}`} onError={onImgError} />
            </>
          )
        },
        { option: 'TEXT', align: 'left', label: tempName },
        { option: 'STATUS', align: 'center', label: tempStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled || tempIsDefault === '1' },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled || tempIsDefault === '1' }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => onActive(Number(objRenderData.id), 'ACTIVE')}
        oninactive={() => onActive(Number(objRenderData.id), 'INACTIVE')}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => onDelete(objRenderData.obj)}
      />
    )
  }

  return (
    <div className={classes.root}>
      <Box sx={{ display: 'flex', flexDirection: 'column', backgroundColor: '#fff', borderRadius: '0.25rem', height: '100%' }}>
        <HeaderCard text={t('TEMPLATE_DOCTOR_NOTE.TITLE')} />
        <Box sx={{ flex: '1 1 auto', p: '16px' }}>
          <Box className={'pl-xl-3'} sx={{ mb: 0 }}>
            <Grid container sx={{ alignItems: 'center' }}>
              <Grid item md={8}>
                <Grid container className="flax-row">
                  <Grid item sm={12} md={6} xl={4} sx={{ pt: '8px' }}>
                    <InputTextSearch
                      keyInput={'search'}
                      label={t('INPUT.SEARCH')}
                      value={search}
                      onchange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                      }}
                      onClear={(event) => {
                        setSearch('')
                        setPage(1)
                      }}
                    />
                  </Grid>
                  <Grid item sm={12} md={6} xl={4} sx={{ pl: { md: 0 }, pt: '8px' }}>
                    <FilterSelect
                      onchange={(event) => {
                        const value = event.target.value
                        if (value) {
                          handleChangeStatus(value)
                          setPage(1)
                        }
                      }}
                      renderValue={() => `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}`}
                      label={t('STATUS.LABEL')}
                      selectId="select-status"
                      value={status}
                      labelId="label-status"
                      options={[
                        <MenuItem key="1" value="ALL">
                          {t('STATUS.ALL')}
                        </MenuItem>,
                        <MenuItem key="2" value="ACTIVE">
                          {t('STATUS.ACTIVE')}
                        </MenuItem>,
                        <MenuItem key="3" value="INACTIVE">
                          {t('STATUS.INACTIVE')}
                        </MenuItem>
                      ]}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item md={4} sx={{ mt: { xs: '8px', md: 'auto' } }}>
                <div className="pr-xl-2">
                  <ButtonCustom mode="add" disabled={permissions.isCreate.disabled} onClick={() => dispatch(showModal())} textButton={t('TEMPLATE_DOCTOR_NOTE.BUTTON.ADD')} className="w-auto ml-auto d-flex mt-auto" />
                </div>
              </Grid>
            </Grid>
          </Box>

          <TableWidth className={'mt-3 pl-xl-3 pr-xl-2'}>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              onSort={onRequestSort}
              setPageLimit={handleChangeRowsPerPage}
              setPage={handleChangePage}
              rowCount={rowCount}
              headCells={headCells}
              rowsData={templates.map((val, i) => renderData(val, i))}
            />
          </TableWidth>
        </Box>
      </Box>

      <ModalCustom
        modalStyle={classes.modal}
        title={templateId ? t('TEMPLATE_DOCTOR_NOTE.TITLE_UPDATE') : t('TEMPLATE_DOCTOR_NOTE.TITLE_CREATE')}
        component={
          <div className="pb-2">
            {templateId ? (
              <div>
                <Box component="form" sx={{ mx: 0, mt: 0, mb: '16px', p: 0, display: 'inline-flex', width: '100%' }}>
                  <Box component="label" sx={{ mr: 'auto' }}>{t('INPUT.LABEL_STATUS')}</Box>
                  <Switch id="custom-switch" onChange={() => setTemplateStatus(templateStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')} checked={templateStatus === 'ACTIVE' ? true : false} style={{ marginLeft: 'auto' }} />
                </Box>
              </div>
            ) : (
              ''
            )}

            <Grid container>
              <Grid item sm={12}>
                <UploadImage setValue={uploadImage} pathFile={imgData} />
              </Grid>
              <Grid item sm={12}>
                <InputTextField
                  onchange={(event) => {
                    setTemplateName(event.target.value)
                    clearErrorMessage()
                  }}
                  inputProps={{ maxLength: 100 }}
                  value={templateName}
                  label={t('TEMPLATE_DOCTOR_NOTE.INPUT.TEMPLATE_DOCTOR_NOTE_NAME')}
                  helperText={
                    errorMessage.TEMPLATE_DOCTOR_NOTE_NAME ||
                    errorMessage.IS_DUPLICATE_TEMPLATE_DOCTOR_NOTE_NAME ||
                    errorMessage.TEMPLATE_DOCTOR_NOTE_NAME_STRING_BASE ||
                    errorMessage.TEMPLATE_DOCTOR_NOTE_NAME_STRING_EMPTY ||
                    errorMessage.TEMPLATE_DOCTOR_NOTE_NAME_MAXIMUM_LENGTH ||
                    errorMessage.TEMPLATE_DOCTOR_NOTE_NAME_ANY_REQUIRED
                  }
                  required={true}
                  style={{ margin: '1rem 0' }}
                />
              </Grid>

            </Grid>
          </div>
        }
        onReset={resetForm}
        onSubmit={templateId ? submitEdit : submit}
        textBtnCancel={t('TEMPLATE_DOCTOR_NOTE.BUTTON.CANCEL')}
        textBtnConfirm={t('TEMPLATE_DOCTOR_NOTE.BUTTON.SAVE')}
      />
    </div>
  )
}
